import localforage from "localforage";
import { createTransform } from "redux-persist";
import { estimateStorageHealth } from "../../offline/storageHealth";
import { isOfflineFeatureEnabled } from "../../offline/featureFlag";

/**
 * Slices big enough to be worth skipping when the browser has given us almost no
 * quota (private/incognito, or a full device). `routesStore` is deliberately NOT
 * in this list: the current route is the mid-route screen and is small enough to
 * always be worth attempting.
 */
const LARGE_SLICES = ["householdsStore", "visitsStore", "categoriesStore"];

let storageRestricted = false;

/** Set from a real storage estimate at boot; exported for tests. */
export const setStorageRestricted = (value: boolean): void => {
  storageRestricted = value;
};

export const initStorageGuard = async (): Promise<void> => {
  setStorageRestricted((await estimateStorageHealth()) === "restricted");
};

export const storageGuardTransform = createTransform(
  (inboundState: any, key) => {
    // No row cap and no TTL by design: a volunteer can be sent to any household in
    // the workspace, so truncating the list guarantees the one contact they need is
    // the one that is missing, and stale data in a dead zone still beats no data.
    // The largest real workspace is ~3k households (~4 MB serialised), which is well
    // inside a normal IndexedDB quota.
    if (storageRestricted && LARGE_SLICES.includes(String(key))) {
      return {};
    }
    return { ...inboundState, persistedAt: Date.now() };
  },
  // Nothing is ever discarded on read. Freshness comes from refetching on
  // reconnect, never from deleting what the volunteer already has.
  (outboundState: any) => outboundState
);

const PERSISTED_SLICES = [
  "routesStore",
  "householdsStore",
  "visitsStore",
  "categoriesStore",
];

export const persistConfig = {
  key: "connectyid",
  version: 1,
  storage: localforage,
  // An empty whitelist when the feature is off for this user: redux-persist still
  // wraps the reducer (the store is built once, at boot) but writes and restores
  // nothing, so a user who was never opted in gets byte-for-byte today's behaviour.
  whitelist: isOfflineFeatureEnabled() ? PERSISTED_SLICES : [],
  transforms: [storageGuardTransform],
};
